import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, input } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Observable, mergeMap } from 'rxjs';

import { MAIN_NAVIGATION_MAX_SUB_CATEGORIES_DEPTH } from 'ish-core/configurations/injection-keys';
import { AccountFacade } from 'ish-core/facades/account.facade';
import { ShoppingFacade } from 'ish-core/facades/shopping.facade';
import { IconModule } from 'ish-core/icon.module';
import { ContentPageletView } from 'ish-core/models/content-view/content-view.model';
import { NavigationCategory } from 'ish-core/models/navigation-category/navigation-category.model';
import { InjectSingle } from 'ish-core/utils/injection';
import { CMSComponent } from 'ish-shared/cms/models/cms-component/cms-component.model';
import { ShellModule } from 'ish-shell/shell.module';

@Component({
  selector: 'custom-cms-main-navigation-category',
  templateUrl: './cms-main-navigation-category.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterModule, ShellModule, IconModule],
})
export class CMSMainNavigationCategoryComponent implements OnInit, CMSComponent {
  private shoppingFacade = inject(ShoppingFacade);
  private accountFacade = inject(AccountFacade);
  mainNavigationMaxSubCategoriesDepth = inject<InjectSingle<typeof MAIN_NAVIGATION_MAX_SUB_CATEGORIES_DEPTH>>(
    MAIN_NAVIGATION_MAX_SUB_CATEGORIES_DEPTH
  );

  readonly pagelet = input<ContentPageletView>(undefined);

  navigationCategory$: Observable<NavigationCategory>;
  private openedCategories: string[] = [];

  ngOnInit(): void {
    const pagelet = this.pagelet();
    if (pagelet.hasParam('NavigationCategory')) {
      this.mainNavigationMaxSubCategoriesDepth ||= pagelet.numberParam('MaxSubCategoriesDepth');
      const categoryRefId = pagelet.stringParam('NavigationCategory');
      this.navigationCategory$ = categoryRefId && this.getNavigationCategory$(categoryRefId);
    }
  }

  getNavigationCategory$(categoryRefId: string): Observable<NavigationCategory> {
    return this.accountFacade.isLoggedIn$.pipe(
      mergeMap(() => this.shoppingFacade.singleNavigationCategories$(categoryRefId))
    );
  }

  subMenuShow(subMenu: HTMLElement) {
    subMenu.classList.add('hover');
  }

  /**
   * Handle sub menu hide.
   * Removes hover class from rendered element.
   *
   * @param subMenu The rendered sub menu element.
   */
  subMenuHide(subMenu: HTMLElement) {
    subMenu.classList.remove('hover');
  }

  /**
   * Indicate if specific category is expanded.
   *
   * @param category The category item.
   */
  isOpened(uniqueId: string): boolean {
    return this.openedCategories.includes(uniqueId);
  }

  /**
   * Toggle category open state.
   *
   * @param category The category item.
   */
  toggleOpen(uniqueId: string) {
    const index = this.openedCategories.findIndex(id => id === uniqueId);
    index > -1 ? this.openedCategories.splice(index, 1) : this.openedCategories.push(uniqueId);
  }
}
